import { debounce } from '@a-type/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
export function useDebounced(value, delay, immediate = false) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        if (immediate) {
            setDebouncedValue(value);
            return;
        }
        const timeout = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => clearTimeout(timeout);
    }, [value, delay, immediate]);
    return debouncedValue;
}
export function useDebouncedCallback(callback, delay) {
    const stableCallback = useRef(callback);
    stableCallback.current = callback;
    const [debouncedCallback] = useState(() => {
        let timeout;
        function debounced(...args) {
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                stableCallback.current(...args);
            }, delay);
        }
        debounced.immediate = (...args) => {
            clearTimeout(timeout);
            stableCallback.current(...args);
        };
        debounced.cancel = () => {
            clearTimeout(timeout);
        };
        return debounced;
    });
    return debouncedCallback;
}
export function useDebouncedState(syncedValue, onCommitChange, debounceTime = 500) {
    const [localValue, setLocalValue] = useState(undefined);
    // specifically doing this so the commit still happens even
    // if the component unmounts
    const commitChangeRef = useRef(onCommitChange);
    commitChangeRef.current = onCommitChange;
    const debouncedCommit = useMemo(() => debounce((value) => {
        commitChangeRef.current(value);
        setLocalValue(undefined);
    }, debounceTime), []);
    const setValue = useCallback((v) => {
        setLocalValue(v);
        debouncedCommit(v);
    }, [debouncedCommit]);
    return [localValue ?? syncedValue, setValue];
}
//# sourceMappingURL=useDebounce.js.map